import { CommonModule } from "@angular/common";
import {
  AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output
  , SimpleChanges, ViewChild, ViewEncapsulation, computed, input,
  model
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { TriStateCheckBoxComponent } from '@myapp/features/widgets/components';
import { AuthorizationService } from '@myapp/modules/fwcore/services';
import { KENDO_SVGICON } from "@progress/kendo-angular-icons";
import { CheckBoxModule, TextBoxModule } from "@progress/kendo-angular-inputs";
import { KENDO_LISTVIEW, ListViewComponent } from '@progress/kendo-angular-listview';
import { SVGIcon, searchIcon } from '@progress/kendo-svg-icons';
import { Subject } from "rxjs";
import { debounceTime, finalize, map, takeUntil, tap } from "rxjs/operators";
import { ControlResizerOptions, ControlResizerService } from "../../../../../../services/framework";
import { RequestUserPermission, RequestUserSummary, ResponseUserSummary, UserSummary, UserSummaryChecked } from "../../../../../modules/fwcore";
import { AdminService } from '../../../services';

@Component({
  selector: 'app-user-list',
  standalone: true,
  imports: [CommonModule, TextBoxModule, FormsModule, TriStateCheckBoxComponent, CheckBoxModule, KENDO_SVGICON, KENDO_LISTVIEW],
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class UserListComponent implements OnChanges, OnDestroy {

  // ---------------- New Input/Outputs ----------------

  public heightControl = model(300);
  public showToolbar = input(false);
  public showLicenseFilter = input(false);
  public includeEmail = input(false);
  public heightListView = computed(() => {

    if (this.showToolbar()) {

      let h = 0;

      // Take the difference between the top of the toolbar and the top of the listview, to get the height of the toolbar
      // As the license filter is not yet rendered, we need to calculate the height of the toolbar manually instead of using the height of the toolbar
      const tb = this.elem.nativeElement?.querySelector('.isg-userlist-toolbar')?.getBoundingClientRect();
      const ul = this.elem.nativeElement?.querySelector('.isg-userlist-lb-wrapper')?.getBoundingClientRect();

      const w = ul.top - tb.top;

      if (w) {
        h = w;
      }

      // reduce the height of the list by the height of the info panel
      return this.heightControl() - h;
    }
    else
      return this.heightControl();

  });

  // ---------------- traditional input/outputs ----------------

  @Input() public moduleId: string | undefined | null = null;
  @Input() public companyId: string | null = null;
  @Input() public licenseId: string | null = null;
  @Input() public refApplicationId: string | null = null;
  @Input() public assignedTolicenseId: string | null = null;
  @Input() public roleId: string | undefined | null = null;
  @Input() public showCheckbox: boolean = false;
  @Input() public showLicensedIcon: boolean = false;

  @Input() public minHeight: number = 200;
  @Input() public widthThreshold: number = 600;

  @Input()
  set checkedIds(ids: string[]) {

    this.checkIdCache = ids;

    this.dataItems.forEach((item) => {
      item.checked = ids.includes(item.id!);
    });
  }
  get checkedIds(): string[] {

    const checkedItemIds: string[] = this.dataItems
      .filter((item) => item.checked)
      .map((item) => item.id!);

    return checkedItemIds ?? [];
  }

  @Input()
  set showLicenseUsersOnly(value: boolean) {
    if (value == true) {
      this.licenseId = this.assignedTolicenseId;
      this.activeLicenseUser = true;
    }
    else {
      this.licenseId = null;
      this.activeLicenseUser = false;
    }

    this.debouncedSubject.next();
  }
  get showLicenseUsersOnly(): boolean {
    return (!!(this.licenseId) || this.activeLicenseUser);
  }

  @Input()
  set initialized(value: boolean) {
    this.hasInitialized = value;
  }
  get initialized(): boolean {
    return this.hasInitialized;
  }

  @Output() selected: EventEmitter<UserSummary | null> = new EventEmitter();

  //----------------- State properties  ------------------

  protected loading: boolean = true;

  protected destroy$ = new Subject();

  protected dataItems: UserSummaryChecked[] = [];

  protected hasInitialized: boolean = false;
  protected activeLicenseUser: boolean = true;

  //----------------- Control handling   ------------------

  @ViewChild(ListViewComponent, { static: false })
  protected listview!: ListViewComponent;

  protected debouncedSubject = new Subject<void>();

  protected startsWithSearch: string = "";

  protected checkIdCache: string[] = [];

  protected searchIcon: SVGIcon = searchIcon;

  //------------------- public properties / methods --------

  public selectedItem?: UserSummary | null;

  public heightChange(options?: ControlResizerOptions): void {

    let ctrlOptions: ControlResizerOptions = new ControlResizerOptions();
    let boundClientRect = this.elem.nativeElement.getBoundingClientRect()
    ctrlOptions.elemTop = boundClientRect.top;

    if (options) {
      ctrlOptions = { ...options, elemTop: ctrlOptions.elemTop };
    }

    if (this.showToolbar()) {
      ctrlOptions.elemTop = ctrlOptions.elemTop! + 91;
    }

    if (window.innerWidth > this.widthThreshold)
      this.heightControl.set(this.controlResizer.calculateHeight(ctrlOptions));
    else {
      // Do not dynamically resize height if below the width threshold
      this.heightControl.set(this.minHeight);
    }
  }

  public Initialized(): void {

    this.hasInitialized = true;
  }

  public clearSelection() {
    this.selectedItem = null;
    this.listview.focus(undefined);
  }

  public clearCheckedItems() {
    this.checkedIds = [];
  }

  public getCheckedItems(): UserSummary[] {

    const checkedItems: UserSummary[] = this.dataItems
      .filter((item) => item.checked);

    return checkedItems ?? [];
  }

  public getCheckedNames(): string[] {

    const names: string[] = this.dataItems
      .filter((item) => item.checked)
      .map((item) => item.displayName!);

    return names;
  }

  public removeItems(items: UserSummary[] | null) {

    const t = this.dataItems.length ?? 0;

    for (let i = t - 1; i >= 0; i--) {
      let v = this.dataItems[i];
      if (items?.includes(v))
        this.dataItems.splice(i, 1);
    }
  }

  //------------------- Constructor  --------------------------

  constructor(
    public serviceAuth: AuthorizationService
    , protected router: Router
    , protected adminService: AdminService
    , protected controlResizer: ControlResizerService
    , protected elem: ElementRef
  ) {

    // Debounce the get data call
    this.debouncedSubject.pipe(
      debounceTime(300),  // Adjust the debounce time as needed
      takeUntil(this.destroy$) // Cleanup when component is destroyed
    ).subscribe(() => this.getUserSummaries());
  }

  //-------------------- ng  ----------------------------------

  ngOnChanges(changes: SimpleChanges) {

    if (this.hasInitialized) {

      if (this.inputChanges(changes)) {
        this.debouncedSubject.next();
      }
    }
  }

  ngOnDestroy(): void {

    this.destroy$.next();
    this.destroy$.complete();
  }

  //-------------------- event handlers  ------------------------

  protected onClickItemEvent(index: number, dataItem: UserSummary, event: Event): void {

    let newSelection: UserSummary = dataItem;

    if (this.selectedItem == newSelection) {
      this.clearSelection();
      this.selected.emit(this.selectedItem);
    }
    else {
      this.selectedItem = newSelection;
      this.listview.focus(index);
      this.selected.emit(this.selectedItem);
    }

    event.stopPropagation();
  }

  protected onChangeListView(event: any) {
    // will capture items being checked
  }

  protected onClickListView(event: any) {

  }

  protected onFilterChange(query: string): void {

    this.startsWithSearch = query.toLowerCase();
    this.debouncedSubject.next();
  }

  //--------------------- internal functions  ---------------------

  protected inputChanges(changes: SimpleChanges): boolean {

    let update: boolean = false;

    if (changes.moduleId)
      update = true;
    if (changes.companyId)
      update = true;
    if (changes.licenseId)
      update = true;
    if (changes.roleId)
      update = true;


    return update;
  }

  // -------------------- API Processing --------------------------

  protected getUserSummaries(): void {

    if (!this.hasInitialized)
      return;

    this.loading = true;

    const req: RequestUserSummary = {
      moduleId: this.moduleId
      , companyId: this.companyId
      , licenseId: this.licenseId
      , roleId: this.roleId
      , activeUser: true
      , activeLicenseUser: this.activeLicenseUser
      , assignedToLicenseId: this.assignedTolicenseId
      , startsWith: (this.startsWithSearch && this.startsWithSearch.length > 0) ? this.startsWithSearch : null
      , includeEmail: this.includeEmail()
      , applicationId: this.refApplicationId
    };

    this.serviceAuth.getData$<RequestUserSummary, ResponseUserSummary>(req, this.serviceAuth.endpointUserSummary)
      .pipe(takeUntil(this.destroy$)
        , finalize(() => { this.loading = false; })
        , map((resp) => {

          // Iterate over the items array and add the additional property
          const updatedItems: UserSummaryChecked[] = resp.data?.map((item: UserSummary) => ({
            ...item,
            checked: this.checkIdCache.includes(item.id!),
          })) ?? [];

          return updatedItems;
        }))
      .subscribe(
        (items) => {
          this.dataItems = items;
        }
      );
  }

}


